import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileMinus, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ServerDataTable,
  TableSkeleton,
} from "@/components/ui/server-data-table";
import { useServerTable } from "@/components/ui/use-server-table";
import { useSupportDocColumns } from "@/components/support-documents/support-doc-columns";
import { SupportDocListFilters } from "@/components/support-documents/SupportDocListFilters";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { useSupportDocuments } from "@/lib/api/support-documents.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { SupportDocStatusFilter } from "@/lib/api/support-documents";

export const Route = createFileRoute(
  "/companies/$companyId/documentos-soporte/",
)({
  component: SupportDocsIndexPage,
});

function SupportDocsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SupportDocsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function SupportDocsIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SupportDocStatusFilter>("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");

  const columns = useSupportDocColumns();
  const table = useServerTable({
    defaultSort: { id: "emitDate", desc: true },
    defaultPageSize: 50,
    filterKey: JSON.stringify({ q, status, periodFrom, periodTo }),
  });

  const docsQuery = useSupportDocuments(companyId, {
    ...(q ? { q } : {}),
    status,
    ...(periodFrom ? { periodFrom } : {}),
    ...(periodTo ? { periodTo } : {}),
    ...table.params,
  });

  const hasFilters =
    q.length > 0 || status !== "all" || !!periodFrom || !!periodTo;
  const pagination = docsQuery.data?.pagination;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="documento_soporte" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("supportDocs.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("supportDocs.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("supportDocs.list.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void docsQuery.refetch()}
            disabled={docsQuery.isFetching}
          >
            <RefreshCw
              className={
                docsQuery.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"
              }
            />
            {t("supportDocs.list.refresh")}
          </Button>
          <Button asChild>
            <Link
              to="/companies/$companyId/documentos-soporte/new"
              params={{ companyId }}
            >
              <Plus className="h-4 w-4" />
              {t("supportDocs.list.new")}
            </Link>
          </Button>
        </div>
      </div>

      <SupportDocListFilters
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        periodFrom={periodFrom}
        onPeriodFromChange={setPeriodFrom}
        periodTo={periodTo}
        onPeriodToChange={setPeriodTo}
      />

      {docsQuery.isPending ? (
        <TableSkeleton columns={columns} />
      ) : docsQuery.isError ? (
        <ErrorState
          title={t("supportDocs.list.error_title")}
          description={t("supportDocs.list.error_body")}
          onRetry={() => void docsQuery.refetch()}
          retryLabel={t("common.retry")}
          error={docsQuery.error}
        />
      ) : docsQuery.data.documents.length === 0 ? (
        <EmptyState companyId={companyId} hasFilters={hasFilters} />
      ) : (
        <ServerDataTable
          columns={columns}
          data={docsQuery.data.documents}
          pageCount={pagination?.totalPages ?? 1}
          rowCount={pagination?.total ?? 0}
          sorting={table.sorting}
          onSortingChange={table.onSortingChange}
          pagination={table.pagination}
          onPaginationChange={table.onPaginationChange}
          getRowId={(document) => document.id}
          onRowClick={(document) =>
            void navigate({
              to: "/companies/$companyId/documentos-soporte/$documentId",
              params: { companyId, documentId: document.id },
            })
          }
          isFetching={docsQuery.isFetching}
        />
      )}
    </div>
  );
}

function EmptyState({
  companyId,
  hasFilters,
}: {
  companyId: string;
  hasFilters: boolean;
}) {
  const { t } = useTranslation();
  if (hasFilters) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">
            {t("supportDocs.list.empty_search_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("supportDocs.list.empty_search_body")}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <FileMinus className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">
            {t("supportDocs.list.empty_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("supportDocs.list.empty_body")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/documentos-soporte/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("supportDocs.list.new")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
